import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
    collection,
    addDoc,
    updateDoc,
    doc,
    getDocs,
    query,
    where,
} from "firebase/firestore";

const Users = () => {
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userPhoneNumber, setUserPhoneNumber] = useState("");
    const [userGender, setUserGender] = useState("");
    const [userisserviceprovider, setUserisserviceprovider] = useState(false);

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchPhone, setSearchPhone] = useState("");

    const [expandedUserId, setExpandedUserId] = useState(null);
    const [bookingsMap, setBookingsMap] = useState({});

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchUsers();
    }, []);

    // 🔹 Fetch Users
    const fetchUsers = async () => {
        try {
            const snapshot = await getDocs(collection(db, "users"));
            const list = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((u) => u.anonymousUser === false);

            setUsers(list);
            setFilteredUsers(list);
        } catch (err) {
            console.error("Error fetching users:", err);
        }
    };

    // ➕ Add User
    const handleAddUser = async (e) => {
        e.preventDefault();

        if (!userName || !userEmail || !userPhoneNumber || !userGender) {
            setError("All fields are required.");
            return;
        }

        if (userPhoneNumber.length !== 10) {
            setError("Enter a valid 10-digit phone number.");
            return;
        }

        setLoading(true);
        try {
            const ref = await addDoc(collection(db, "users"), {
                name: userName,
                email: userEmail,
                // E.164 to match Firebase Auth (the +91 prefix is fixed in the UI).
                phoneNumber: `+91${userPhoneNumber}`,
                gender: userGender,
                isserviceprovider: userisserviceprovider,
                anonymousUser: false,
                fcmtoken: "",
            });

            await updateDoc(doc(db, "users", ref.id), { id: ref.id });

            setUserName("");
            setUserEmail("");
            setUserPhoneNumber("");
            setUserGender("");
            setUserisserviceprovider(false);
            setError("");

            fetchUsers();
        } catch (err) {
            console.error(err);
            setError("Failed to add user");
        } finally {
            setLoading(false);
        }
    };

    // 📄 Fetch User Bookings
    const fetchUserBookings = async (userId) => {
        try {
            console.log("🔍 Fetching bookings for userId:", userId);
            const q = query(
                collection(db, "bookings"),
                where("userId", "==", userId)
            );
            const snap = await getDocs(q);

            const bookings = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort(
                    (a, b) =>
                        b.dateTime?.toDate() - a.dateTime?.toDate()
                );

            setBookingsMap((prev) => ({ ...prev, [userId]: bookings }));
        } catch (err) {
            console.error("Error fetching bookings:", err);
        }
    };
    const handleBackToUsers = () => {
        setExpandedUserId(null);
    }
    // 🔍 Search
    const handleSearch = () => {
        if (!searchPhone) {
            setFilteredUsers(users);
            return;
        }

        setFilteredUsers(
            users.filter((u) =>
                u.phoneNumber?.toString().includes(searchPhone.trim())
            )
        );
    };
    const getDaysLeft = (validTill) => {
        if (!validTill) return "—";

        const expiry = validTill.toDate
            ? validTill.toDate()
            : new Date(validTill);

        const diff =
            Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));

        if (diff <= 0) return "Expired";

        return `${diff} days left`;
    };


    return (
        <div className="h-screen p-6 bg-gray-50 overflow-auto">
            <h1 className="text-3xl font-bold mb-6">Users</h1>

            {error && <p className="text-red-600 mb-4">{error}</p>}

            {/* ➕ Add User */}
            <form
                onSubmit={handleAddUser}
                className="grid grid-cols-2 gap-4 mb-8 bg-white p-6 rounded shadow"
            >
                <input className="p-3 border rounded" placeholder="Name"
                    value={userName} onChange={(e) => setUserName(e.target.value)} />
                <input className="p-3 border rounded" placeholder="Email"
                    value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
                <div className="flex items-center overflow-hidden rounded border">
                    <span className="select-none border-r bg-gray-50 px-3 py-3 text-gray-600">
                        +91
                    </span>
                    <input
                        className="min-w-0 flex-1 bg-transparent p-3 focus:outline-none"
                        placeholder="10-digit phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={userPhoneNumber}
                        onChange={(e) =>
                            setUserPhoneNumber(e.target.value.replace(/\D/g, "").slice(-10))
                        }
                    />
                </div>
                <input className="p-3 border rounded" placeholder="Gender"
                    value={userGender} onChange={(e) => setUserGender(e.target.value)} />

                <label className="col-span-2 flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={userisserviceprovider}
                        onChange={(e) => setUserisserviceprovider(e.target.checked)}
                    />
                    Is Service Provider
                </label>

                <button className="col-span-2 bg-blue-600 text-white p-3 rounded">
                    {loading ? "Adding..." : "Add User"}
                </button>
            </form>

            {/* 🔍 Search */}
            <div className="flex gap-3 mb-6">
                <input
                    className="p-3 border rounded w-80"
                    placeholder="Search by phone"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                />
                <button onClick={handleSearch} className="bg-blue-600 text-white px-5 rounded">
                    Search
                </button>
                <button
                    onClick={() => {
                        setSearchPhone("");
                        setFilteredUsers(users);
                    }}
                    className="bg-gray-400 text-white px-5 rounded"
                >
                    Reset
                </button>
            </div>

            {/* 📋 Users Table */}
            <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-[1500px] table-fixed">
                    <thead className="bg-gray-100 sticky top-0">
                        <tr>
                            <th className="p-4 w-[220px]">User ID</th>
                            <th className="p-4 w-[180px]">Name</th>
                            <th className="p-4 w-[220px]">Email</th>
                            <th className="p-4 w-[150px]">Phone</th>
                            <th className="p-4 w-[120px]">Gender</th>
                            <th className="p-4 w-[320px]">Geolocation</th>
                            <th className="p-4 w-[120px]">Cash Points</th>
                            <th className="p-4 w-[180px]">Validity</th>
                            <th className="p-4 w-[150px]">Provider</th>
                            <th className="p-4 w-[150px]">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((u) => (
                            <tr key={u.id} className="border-t hover:bg-gray-50">
                                <td className="p-4 break-words">{u.id}</td>
                                <td className="p-4">{u.name}</td>
                                <td className="p-4 break-words">{u.email}</td>
                                <td className="p-4">{u.phoneNumber}</td>
                                <td className="p-4">{u.gender}</td>
                                <td className="p-4 break-words">
                                    {u.geolocation || "—"}
                                </td>
                                <td className="p-4 font-medium">
                                    {u.cashpoints ?? "0"}
                                </td>

                                <td className="p-4 text-sm">
                                    {getDaysLeft(u.cashPointsValidTill)}
                                </td>
                                <td className="p-4">
                                    {u.isserviceprovider ? "Yes" : "No"}
                                </td>

                                <td className="p-4">
                                    <button
                                        onClick={() => {
                                            setExpandedUserId(u.id);
                                            if (!bookingsMap[u.id]) {
                                                fetchUserBookings(u.id);
                                            }
                                        }}
                                        className="text-blue-600 underline"
                                    >
                                        View Bookings
                                    </button>
                                    <button
                                        onClick={async () => {
                                            const pointsStr = prompt("Enter cash points");
                                            const daysStr = prompt("Validity in days", "15");

                                            if (!pointsStr || !daysStr) return;

                                            const points = parseInt(pointsStr, 10);
                                            const days = parseInt(daysStr, 10);

                                            if (isNaN(points) || isNaN(days)) {
                                                alert("Invalid input");
                                                return;
                                            }

                                            try {
                                                const userRef = doc(db, "users", u.id);

                                                // ✅ KEEP OLD LOGIC
                                                const expiryDate = new Date();
                                                expiryDate.setHours(23, 59, 59, 999);
                                                expiryDate.setDate(expiryDate.getDate() + days);

                                                // ✅ UPDATE USER (same as before, but ADD)
                                                const existingPoints = parseInt(u.cashpoints ?? "0", 10);
                                                const updatedPoints = existingPoints + points;

                                                await updateDoc(userRef, {
                                                    cashpoints: updatedPoints.toString(),
                                                    cashPointsValidTill: expiryDate,
                                                });

                                                console.log("✅ User updated");

                                                // ✅ ADD HISTORY (NEW, SAFE)
                                                await addDoc(collection(db, "cashpoints_history"), {
                                                    userId: u.id,
                                                    type: "credit",
                                                    points: points,
                                                    reason: "Cash Points Granted",
                                                    createdAt: new Date(),
                                                });

                                                console.log("✅ History added");

                                                // ✅ PUSH NOTIFICATION (OLD)
                                                await fetch(
                                                    "https://us-central1-local-helperrr.cloudfunctions.net/sendDashboardNotification",
                                                    {
                                                        method: "POST",
                                                        headers: {
                                                            "Content-Type": "application/json",
                                                            Authorization: `Bearer ${u.id}`,
                                                        },
                                                        body: JSON.stringify({
                                                            title: "🎉 Cash Points Added!",
                                                            description: `₹${points} added to your wallet. Valid for ${days} days.`,
                                                            userId: u.id,
                                                        }),
                                                    }
                                                );

                                                alert("Cash points credited successfully ✅");
                                                fetchUsers();
                                            } catch (err) {
                                                console.error("❌ Cashpoint credit failed:", err);
                                                alert("Failed to credit cash points. Check console.");
                                            }
                                        }}


                                        className="text-green-600 underline mt-2 block"
                                    >
                                        Grant Cash Points
                                    </button>

                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>


            {/* 📄 Booking Details Section */}
            {expandedUserId && (
                <div className="h-screen overflow-y-auto p-4 bg-gray-50">
                    <button
                        onClick={handleBackToUsers}
                        className="text-blue-600 hover:underline mb-4 inline-block"
                    >
                        ← Back to Users
                    </button>
                    <div className="bg-white p-5 shadow rounded-lg border">
                        <h2 className="text-xl font-semibold mb-4">Booking History</h2>
                        {bookingsMap[expandedUserId]?.length > 0 ? (
                            <ul className="space-y-4">
                                {bookingsMap[expandedUserId].map((booking) => (
                                    <li
                                        key={booking.id}
                                        className="border p-4 rounded-md bg-gray-50 shadow-sm"
                                    >
                                        <p><strong>Service:</strong> {booking.categoryName}</p>
                                        <p><strong>Status:</strong> {booking.status}</p>
                                        <p><strong>Date:</strong> {booking.dateTime?.toDate().toLocaleString()}</p>
                                        <p><strong>Address:</strong> {booking.address}</p>
                                        <p>
                                            <strong>Provider:</strong> {booking.serviceProviderName} (
                                            {booking.providerPhoneNumber})
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-600">No bookings found.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
